import type { ChatElement } from '../types';

/**
 * Computes how many elements from the tail of the array are still actively updating.
 *
 * Active elements are those that can still change:
 * - Messages with isComplete: false (streaming)
 * - Tools with state.type 'loading' or 'approval_request'
 *
 * Returns the count from the **leftmost** active element to the end of the
 * array.  This ensures that completed elements interspersed between active
 * ones (e.g. parallel tools finishing out of order) stay in the live area
 * so they can be re-rendered when their neighbours update.
 *
 * @param elements - The array of chat elements
 * @returns The count of elements from the leftmost active element to the end
 *
 * @example
 * // [msg(complete), msg(complete), tool(loading)] => returns 1
 * // [msg(complete), msg(streaming)] => returns 1
 * // [msg(complete), tool(complete)] => returns 0
 * // [msg(complete), msg(streaming), tool(loading)] => returns 2
 * // [msg(complete), tool(loading), tool(complete), tool(loading)] => returns 3
 */
export function computeActiveElementCount(elements: ChatElement[]): number {
  const isElementActive = (el: ChatElement): boolean => {
    if (el.type === 'message') {
      return !el.isComplete;
    }
    if (el.type === 'tool') {
      return el.state.type === 'loading' || el.state.type === 'approval_request';
    }
    // Error elements are never active
    return false;
  };

  // Find the leftmost active element; everything from there to the end
  // must stay in the live area so Ink can re-render it.
  for (let i = 0; i < elements.length; i++) {
    if (isElementActive(elements[i])) {
      return elements.length - i;
    }
  }
  return 0;
}
